import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { qualifiesForTpRechargeProfile } from '@uo/shared';
import { authAPI } from '../api/auth';
import { usersAPI } from '../api/users';
import { useAuthStore, type UserProfile } from '../store/auth';
import { useActiveAccountStore } from '../store/activeAccount';
import { useUiStore } from '../store/ui';
import { useTxPasswordGate } from '../hooks/useTxPasswordGate';
import { Button } from '../components/ui/Button';
import { ModalSheet } from '../components/ui/ModalSheet';
import { ThemedPanel } from '../components/ui/ThemedSurface';
import { WalletHeader } from '../components/wallet';
import { GridFeatureTile, FEATURE_TILE_GRID_CLASS, FeatureTileCell } from '../components/wallet/GridFeatureTile';
import {
  ME_ASSET_ITEM_DEFS,
  ME_COLORS,
  ME_FINANCE_ITEM_DEFS,
  ME_MARKET_ITEM_DEFS,
  ME_SYSTEM_ITEM_DEFS,
  ME_ITEM_COLORS,
  PROD_ME_SHOW_INVITE_BANNER,
  filterProdMeKeys,
  filterProdRoutes,
  isProdRouteVisible,
} from '@/constants';

function ChevronRightIcon({ width = 10, height = 10 }: { width?: number; height?: number } = {}) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={width} height={height} viewBox="0 0 10 10" fill="none">
      <path fillRule="evenodd" clipRule="evenodd" d="M8.41847 5L5.33139 1.91291C5.14833 1.72985 5.14833 1.43305 5.33139 1.25C5.51444 1.06694 5.81124 1.06694 5.9943 1.24999L9.30236 4.55805C9.54643 4.80213 9.54643 5.19786 9.30236 5.44194L5.9943 8.75C5.81124 8.93305 5.51444 8.93305 5.33139 8.75C5.14833 8.56694 5.14833 8.27014 5.33139 8.08708L8.41847 5Z" fill={ME_COLORS.text}/>
    </svg>
  );
}

interface GridItem { itemKey: string; label: string; icon?: string; color?: string; onClick?: () => void }

function MeSection({
  title,
  subtitle,
  items,
}: {
  title: string;
  subtitle?: string;
  items: GridItem[];
}) {
  const [open, setOpen] = useState(true);

  return (
    <div className="overflow-hidden flex flex-col gap-2 rounded-xl p-3 border" style={{ borderColor: ME_COLORS.inviteBorder, background: ME_COLORS.glassBg }}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-end gap-2">
          <div className="text-sm font-semibold leading-none" style={{ color: ME_COLORS.text }}>{title}</div>
          {subtitle ? (
            <div className="mt-1 truncate text-[10px] leading-none" style={{ color: ME_COLORS.muted }}>
              {subtitle}
            </div>
          ) : null}
        </div>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex size-7 items-center justify-center rounded-full active:opacity-75"
          style={{ background: 'rgba(255,255,255,0.06)', color: ME_COLORS.muted }}
          aria-expanded={open}
          aria-label={open ? 'collapse' : 'expand'}
        >
          <svg
            className={open ? 'rotate-180' : undefined}
            viewBox="0 0 24 24"
            width="16"
            height="16"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path d="m7 10 5 5 5-5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
      {open && (
        <div className={`${FEATURE_TILE_GRID_CLASS}`}>
          {items.map((item) => (
            <FeatureTileCell key={item.itemKey}>
              <GridFeatureTile
                label={item.label}
                icon={item.icon}
                labelColor={item.color ?? ME_COLORS.text}
                onClick={item.onClick}
                className="h-full w-full"
              />
            </FeatureTileCell>
          ))}
        </div>
      )}
    </div>
  );
}

function ProfileMetric({ value, label }: { value: ReactNode; label: string }) {
  return (
    <div className="flex min-w-0 flex-col items-center gap-0.5">
      <div className="max-w-full text-center text-xs font-semibold leading-tight text-white line-clamp-2">{value}</div>
      <div className="max-w-full truncate text-[10px]" style={{ color: ME_COLORS.text }}>{label}</div>
    </div>
  );
}

function repeatSymbol(symbol: string, count: number) {
  return symbol.repeat(Math.max(0, Math.min(count, 5)));
}

interface ProfileMetrics {
  trading_credit: number;
  invest_star: number;
  account_category: string;
  service_level: string;
  reward_level_code?: string | null;
  space_station: boolean;
}

export function Me() {
  const { t } = useTranslation();
  const profile = useAuthStore((s) => s.profile);
  const activeId = useActiveAccountStore((s) => s.accountId);
  const activeUsername = useActiveAccountStore((s) => s.username);
  const setProfile = useAuthStore((s) => s.setProfile);
  const clear = useAuthStore((s) => s.clear);
  const navigate = useNavigate();
  const openDrawer = useUiStore((s) => s.openDrawer);
  const { gate, modal: txPasswordModal } = useTxPasswordGate({
    title: t('common.txPassword', '交易密码'),
    hint: t('me.enterTxPasswordToEnter', '请输入交易密码后进入'),
  });

  const { data: viewedProfile } = useQuery({
    queryKey: ['user', 'profile', activeId ?? 'self'],
    queryFn: () => usersAPI.getProfile(activeId ? { account_id: activeId } : undefined) as Promise<UserProfile & { profile_metrics?: ProfileMetrics }>,
    staleTime: 30_000,
  });

  const profileQuery = useQuery({
    queryKey: ['user', 'profile'],
    queryFn: () => authAPI.me(),
  });
  useEffect(() => {
    if (profileQuery.data) setProfile(profileQuery.data as UserProfile);
  }, [profileQuery.data, setProfile]);

  const displayProfile = activeId && viewedProfile
    ? (viewedProfile as UserProfile)
    : profile;
  const profileMetrics: ProfileMetrics = viewedProfile?.profile_metrics
    ?? profile?.profile_metrics
    ?? {
      trading_credit: 0,
      invest_star: 0,
      account_category: '—',
      service_level: t('me.notReachedR'),
      space_station: false,
    };

  async function logout() {
    try { await authAPI.logout({ refresh_token: useAuthStore.getState().refreshToken ?? undefined }); } catch { /* ignore */ }
    useActiveAccountStore.getState().clearActiveAccount();
    clear();
    navigate('/login', { replace: true });
  }

  const displayPlayerId = displayProfile?.player_number?.trim() || null;
  const firstLetter = displayProfile?.username?.[0]?.toUpperCase() ?? '?';
  const [playerIdModalOpen, setPlayerIdModalOpen] = useState(false);
  const [copiedPlayerId, setCopiedPlayerId] = useState(false);

  function copyPlayerId() {
    if (!displayPlayerId) return;
    const tryClipboard = navigator.clipboard
      ? navigator.clipboard.writeText(displayPlayerId)
      : Promise.reject();
    tryClipboard.catch(() => {
      const el = document.createElement('textarea');
      el.value = displayPlayerId;
      el.style.position = 'fixed';
      el.style.opacity = '0';
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }).finally(() => {
      setCopiedPlayerId(true);
      setTimeout(() => setCopiedPlayerId(false), 2000);
    });
  }

  function openPlayerIdModal() {
    if (!displayPlayerId) return;
    setPlayerIdModalOpen(true);
  }

  function viewPlayerNetwork() {
    if (!displayPlayerId) return;
    setPlayerIdModalOpen(false);
    navigate(`/all-users?root=${encodeURIComponent(displayPlayerId)}`);
  }
  const assetItems = filterProdMeKeys(ME_ASSET_ITEM_DEFS)
    .filter((i) => !i.to || isProdRouteVisible(i.to))
    .filter((i) => {
      if (i.key !== 'me_asset.recharge_tp' && i.key !== 'me_asset.recharge_rp') return true;
      return qualifiesForTpRechargeProfile({
        level_code: displayProfile?.level_code ?? profile?.level_code,
        reward_level_code:
          viewedProfile?.profile_metrics?.reward_level_code
          ?? profile?.profile_metrics?.reward_level_code,
      });
    })
    .map((i) => ({
      itemKey: i.key,
      label: t(i.key),
      icon: i.icon,
      color: i.color,
      onClick:
        i.key === 'me_asset.sell_uo' || i.key === 'me_asset.mp_to_uo'
          ? () => alert(t('common.notDevelopedYet', '暂时未开放'))
          : i.to
            ? () => gate(() => navigate(i.to!))
            : undefined,
    }));
  const marketItems = filterProdMeKeys(ME_MARKET_ITEM_DEFS)
    .filter((i) => !i.to || isProdRouteVisible(i.to))
    .map((i) => ({
      itemKey: i.key,
      label: t(i.key),
      icon: i.icon,
      color: i.color,
      onClick: i.to ? () => gate(() => navigate(i.to!)) : undefined,
    }));
  const financeItems = filterProdMeKeys(ME_FINANCE_ITEM_DEFS)
    .filter((i) => !i.to || isProdRouteVisible(i.to))
    .map((i) => ({
      itemKey: i.key,
      label: t(i.key),
      icon: i.icon,
      color: i.color,
      onClick: i.to ? () => gate(() => navigate(i.to!)) : undefined,
    }));
  const systemItems = filterProdRoutes(ME_SYSTEM_ITEM_DEFS)
    .map((i) => ({
      itemKey: i.key,
      label: t(i.key),
      icon: i.icon,
      color: i.color,
      onClick: i.isLogout
        ? () => void logout()
        : i.to
          ? () => gate(() => navigate(i.to!))
          : undefined,
    }));

  return (
    <div className="relative min-h-full overflow-hidden pb-8" style={{ background: ME_COLORS.pageBg }}>
      <div className="relative mx-auto min-h-screen w-full max-w-[430px]">
        <WalletHeader t={t} onMenu={openDrawer} />

        <section className="relative overflow-hidden pt-[77px] pb-2">
          <img src="/brand/me-hero.png" alt="" className="absolute inset-x-0 top-[77px] h-[268px] w-full object-cover" draggable={false} />
          <div className="absolute inset-x-0 top-[77px] h-[268px]" style={{ background: ME_COLORS.heroFade }} />

          <div className="relative z-10 flex flex-col items-center pt-[59px] text-center">
            <div className="relative grid size-[64px] place-items-center">
              <div className="absolute inset-[-14px] max-w-none">
                <svg xmlns="http://www.w3.org/2000/svg" width="90" height="91" viewBox="0 0 90 91" fill="none">
                  <circle cx="45" cy="46" r="31" fill={ME_COLORS.avatarFill} />
                  <g filter="url(#filter0_ddi_1_379)">
                    <path d="M75.4762 46C75.4762 29.1685 61.8315 15.5238 45 15.5238C28.1685 15.5238 14.5238 29.1685 14.5238 46C14.5238 62.8315 28.1685 76.4762 45 76.4762V78C27.3269 78 13 63.6731 13 46C13 28.3269 27.3269 14 45 14C62.6731 14 77 28.3269 77 46C77 63.6731 62.6731 78 45 78V76.4762C61.8315 76.4762 75.4762 62.8315 75.4762 46Z" fill="url(#paint0_linear_1_379)"/>
                  </g>
                  <defs>
                    <filter id="filter0_ddi_1_379" x="0" y="0" width="90" height="91" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                      <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                      <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                      <feOffset/>
                      <feGaussianBlur stdDeviation="6.5"/>
                      <feComposite in2="hardAlpha" operator="out"/>
                      <feColorMatrix type="matrix" values="0 0 0 0 0.901961 0 0 0 0 0.733333 0 0 0 0 0.196078 0 0 0 1 0"/>
                      <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_1_379"/>
                      <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                      <feOffset dy="-2"/>
                      <feGaussianBlur stdDeviation="6"/>
                      <feComposite in2="hardAlpha" operator="out"/>
                      <feColorMatrix type="matrix" values="0 0 0 0 0.960784 0 0 0 0 0.92549 0 0 0 0 0.388235 0 0 0 1 0"/>
                      <feBlend mode="normal" in2="effect1_dropShadow_1_379" result="effect2_dropShadow_1_379"/>
                      <feBlend mode="normal" in="SourceGraphic" in2="effect2_dropShadow_1_379" result="shape"/>
                      <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                      <feOffset dx="-0.2" dy="0.2"/>
                      <feGaussianBlur stdDeviation="0.5"/>
                      <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
                      <feColorMatrix type="matrix" values="0 0 0 0 0.960784 0 0 0 0 0.92549 0 0 0 0 0.388235 0 0 0 1 0"/>
                      <feBlend mode="normal" in2="shape" result="effect3_innerShadow_1_379"/>
                    </filter>
                    <linearGradient id="paint0_linear_1_379" x1="45" y1="-8" x2="45" y2="78" gradientUnits="userSpaceOnUse">
                      <stop stopColor={ME_COLORS.avatarRingGradientStart}/>
                      <stop offset="1" stopColor={ME_COLORS.avatarRingGradientEnd}/>
                    </linearGradient>
                  </defs>
                </svg>
              </div>
              <span className="relative flex size-[64px] items-center justify-center text-center text-[30px] font-semibold leading-none tracking-normal" style={{ color: ME_COLORS.avatarLetter }}>{firstLetter}</span>
            </div>
            <div className="mt-2 text-[23px] font-semibold leading-none tracking-[1.61px] text-white">
              {displayProfile?.username ?? '—'}
              {activeId ? (
                <span className="ml-2 text-xs font-medium" style={{ color: ME_COLORS.muted }}>
                  ({t('accountSwitcher.subTag', '子账户')})
                </span>
              ) : null}
            </div>
            <button
              type="button"
              onClick={openPlayerIdModal}
              disabled={!displayPlayerId}
              className="mt-2 text-[13px] tracking-[0.91px] underline-offset-2 hover:underline active:opacity-75 disabled:opacity-50 disabled:no-underline"
              style={{ color: ME_COLORS.gold, textShadow: ME_COLORS.playerIdShadow }}
            >
              {t('common.playerId')}: {displayPlayerId ?? '—'}
            </button>
          </div>

          <div className="px-5">
            <ThemedPanel className="relative z-10 mt-[31px] px-2 py-3" theme={{ border: ME_COLORS.inviteBorder, glassBg: ME_COLORS.glassBg }}>
              <div className="grid grid-cols-4 gap-1 text-center">
                <ProfileMetric
                  value={
                    <span className="text-[11px]" style={{ color: ME_COLORS.metricDanger }}>
                      {repeatSymbol('♥', profileMetrics.trading_credit)}
                    </span>
                  }
                  label={t('me.tradingCredit')}
                />
                <ProfileMetric
                  value={
                    <span className="text-[11px]" style={{ color: ME_COLORS.gold }}>
                      {repeatSymbol('★', profileMetrics.invest_star)}
                    </span>
                  }
                  label={t('me.investStar')}
                />
                <ProfileMetric
                  value={
                    <span className="text-[11px] font-semibold" style={{ color: ME_ITEM_COLORS.green }}>
                      {profileMetrics.account_category}
                    </span>
                  }
                  label={t('me.accountType')}
                />
                <ProfileMetric
                  value={
                    <span
                      className="text-[11px] font-semibold"
                      style={{ color: profileMetrics.space_station ? ME_COLORS.gold : ME_COLORS.muted }}
                    >
                      {profileMetrics.service_level}
                    </span>
                  }
                  label={t('me.serviceLevel')}
                />
              </div>
            </ThemedPanel>
          </div>

          {PROD_ME_SHOW_INVITE_BANNER && isProdRouteVisible('/invite') && (
          <div className="px-5 mt-4">
            <button
              type="button"
              onClick={() => navigate('/invite')}
              className="relative z-10 flex h-[68px] overflow-hidden rounded-xl border text-left active:opacity-80 w-full"
              style={{ borderColor: ME_COLORS.inviteBorder }}
            >
              <img
                src="/brand/me-invite-bg.png"
                alt=""
                className="absolute inset-y-0 left-0 h-full w-[70%] object-cover opacity-70"
                style={{ maskImage: ME_COLORS.inviteMask, WebkitMaskImage: ME_COLORS.inviteMask }}
                draggable={false}
              />
              <div className="absolute inset-y-0 left-[25%] w-[50%] blur-3xl" style={{ background: ME_COLORS.inviteEdgeBlur }} />
              <div className="relative z-10 flex h-full min-w-0 flex-1 items-center justify-between gap-3 pl-[93px] pr-5">
                <div className="flex min-w-0 flex-col gap-1 leading-none">
                  <div className="text-[11px] tracking-[0.77px] text-white">{t('me.inviteCode')}</div>
                  <div className="truncate text-[17px] font-semibold tracking-[0.85px]" style={{ color: ME_COLORS.inviteCode, textShadow: ME_COLORS.inviteCodeShadow }}>{profile?.invite_code ?? 'U00001'}</div>
                </div>
                <div className="flex shrink-0 items-center gap-2 rounded-full px-3 py-2 text-[10px] tracking-[1.4px]" style={{ background: ME_COLORS.inviteActionBg, color: ME_COLORS.text }}>
                  {t('me.inviteFriend')}
                  <ChevronRightIcon />
                </div>
              </div>
            </button>
          </div>
          )}

        </section>

        <main className="mt-3 flex flex-col gap-4 px-5">
          {assetItems.length > 0 && (
            <MeSection
              title={t('me.assetSection')}
              items={assetItems}
            />
          )}
          {marketItems.length > 0 && (
            <MeSection
              title={t('me.marketSection')}
              items={marketItems}
            />
          )}
          {financeItems.length > 0 && (
            <MeSection
              title={t('me.financeSection')}
              items={financeItems}
            />
          )}
          {systemItems.length > 0 && (
            <MeSection
              title={t('me.systemSection')}
              items={systemItems}
            />
          )}
        </main>

        <ModalSheet
          open={playerIdModalOpen}
          onClose={() => setPlayerIdModalOpen(false)}
          title={t('common.playerId')}
          footer={
            <div className="flex flex-col gap-2">
              <Button type="button" fullWidth onClick={copyPlayerId}>
                {copiedPlayerId ? `${t('me.playerIdCopied')} ✓` : t('me.copyPlayerId')}
              </Button>
              <Button type="button" fullWidth variant="secondary" onClick={viewPlayerNetwork}>
                {t('me.viewPlayerNetwork')}
              </Button>
            </div>
          }
        >
          <div className="flex flex-col items-center gap-4 py-2 text-center">
            <div
              className="rounded-xl border px-4 py-5 font-mono text-2xl font-semibold tracking-wider"
              style={{ borderColor: ME_COLORS.inviteBorder, color: ME_COLORS.gold }}
            >
              {displayPlayerId}
            </div>
            <p className="text-xs leading-relaxed" style={{ color: ME_COLORS.muted }}>
              {t('me.playerIdHint')}
            </p>
          </div>
        </ModalSheet>
        {txPasswordModal}
      </div>
    </div>
  );
}
